import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Send, Loader2, Mail } from "lucide-react";
import PageHeader from "@/components/site/PageHeader";
import Footer from "@/components/site/Footer";
import { BOOK } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("Thank you! Your message has been sent. 💛");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-twilight text-cream" data-testid="contact-page">
      <PageHeader />
      <main className="mx-auto max-w-2xl px-6 py-20 md:px-10 md:py-28">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-10 text-center"
        >
          <p className="font-body text-sm uppercase tracking-[0.25em] text-gold">Contact</p>
          <h1 className="mt-3 font-display text-4xl font-bold text-cream md:text-6xl">Let's connect</h1>
          <p className="mx-auto mt-4 max-w-lg font-body text-lg text-slate-300">
            Questions, speaking invitations, or a word of encouragement — I'd love to hear from you.
          </p>
          <a
            href={`mailto:${BOOK.email}`}
            data-testid="contact-email"
            className="mt-4 inline-flex items-center gap-2 font-body text-sm text-gold"
          >
            <Mail className="h-4 w-4" /> {BOOK.email}
          </a>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          onSubmit={submit}
          data-testid="contact-form"
          className="space-y-4 rounded-3xl border border-white/10 bg-white/[0.04] p-7 md:p-9"
        >
          <div>
            <label className="mb-1.5 block font-body text-sm text-slate-300">Your name</label>
            <input
              required
              value={form.name}
              onChange={update("name")}
              data-testid="contact-name-input"
              className="w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-cream placeholder:text-slate-500 outline-none transition-[border-color,box-shadow] duration-300 focus:border-gold focus:shadow-[0_0_15px_rgba(251,191,36,0.3)]"
              placeholder="Jane Doe"
            />
          </div>
          <div>
            <label className="mb-1.5 block font-body text-sm text-slate-300">Email</label>
            <input
              required
              type="email"
              value={form.email}
              onChange={update("email")}
              data-testid="contact-email-input"
              className="w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-cream placeholder:text-slate-500 outline-none transition-[border-color,box-shadow] duration-300 focus:border-gold focus:shadow-[0_0_15px_rgba(251,191,36,0.3)]"
              placeholder="jane@email.com"
            />
          </div>
          <div>
            <label className="mb-1.5 block font-body text-sm text-slate-300">Message</label>
            <textarea
              required
              rows={5}
              value={form.message}
              onChange={update("message")}
              data-testid="contact-message-input"
              className="w-full resize-none rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-cream placeholder:text-slate-500 outline-none transition-[border-color,box-shadow] duration-300 focus:border-gold focus:shadow-[0_0_15px_rgba(251,191,36,0.3)]"
              placeholder="Share your thoughts, questions, or invitation…"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="contact-submit"
            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-gold px-7 py-3.5 font-medium text-twilight transition-[background-color,box-shadow] duration-300 hover:bg-gold-light hover:shadow-[0_0_25px_rgba(251,191,36,0.55)] disabled:opacity-70"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {sent ? "Send another message" : "Send Message"}
          </button>
        </motion.form>
      </main>
      <Footer minimal />
    </div>
  );
}
